import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
    MapPin,
    Clock,
    ArrowRight,
    CheckCircle,
    XCircle,
    Trophy,
    House,
    Airplane,
} from "@phosphor-icons/react";

function formatItalianDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso + "T00:00:00");
        return d.toLocaleDateString("it-IT", {
            weekday: "long",
            day: "numeric",
            month: "long",
        });
    } catch {
        return iso;
    }
}

export default function Home() {
    const { user } = useAuth();
    const { refresh: refreshNotifs } = useNotifications();
    const [matches, setMatches] = useState([]);
    const [news, setNews] = useState([]);
    const [players, setPlayers] = useState([]);
    const [conv, setConv] = useState(null); // convocation for the upcoming match (player only)
    const [checked, setChecked] = useState({});

    const nextMatch = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        return matches
            .filter((m) => m.date >= today)
            .sort((a, b) => a.date.localeCompare(b.date))[0];
    }, [matches]);

    useEffect(() => {
        (async () => {
            try {
                const [m, n, p] = await Promise.all([
                    api.get("/matches"),
                    api.get("/news"),
                    api.get("/players").catch(() => ({ data: [] })),
                ]);
                setMatches(m.data);
                setNews(n.data);
                setPlayers(p.data);
            } catch (e) {
                if (process.env.NODE_ENV !== "production") {
                    console.warn("home load failed:", e);
                }
            }
        })();
    }, []);

    useEffect(() => {
        if (!nextMatch) return;
        setChecked({});
        (async () => {
            try {
                const { data } = await api.get(`/matches/${nextMatch.id}/convocations`);
                if (user?.role === "player") {
                    const me = players.find((x) => x.user_id === user.id);
                    const mine = me ? data.find((c) => c.player_id === me.id) : null;
                    setConv(mine || null);
                } else {
                    setConv(null);
                }
            } catch (e) {
                if (process.env.NODE_ENV !== "production") {
                    console.warn("convocations load failed:", e);
                }
            }
        })();
    }, [nextMatch, user, players]);

    const respond = async (status) => {
        if (!nextMatch) return;
        try {
            const { data } = await api.put(
                `/matches/${nextMatch.id}/convocations/confirm`,
                { status }
            );
            setConv((c) => (c ? { ...c, status: data.status } : c));
            toast.success(status === "confirmed" ? "Presenza confermata" : "Assenza registrata");
            refreshNotifs();
        } catch (e) {
            toast.error("Non è stato possibile aggiornare la convocazione");
        }
    };

    return (
        <div className="space-y-8" data-testid="home-dashboard">
            {/* Greeting */}
            <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    Ciao 👋
                </div>
                <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight leading-none mt-1">
                    {user?.name?.split(" ")[0]}
                </h1>
            </div>

            {/* Next match hero */}
            {nextMatch ? (
                <NextMatchCard
                    match={nextMatch}
                    convStatus={conv?.status}
                    canRespond={user?.role === "player" && conv}
                    onConfirm={() => respond("confirmed")}
                    onDecline={() => respond("declined")}
                />
            ) : (
                <div
                    data-testid="no-match-empty"
                    className="rounded-2xl hairline bg-card p-8 bg-field text-center"
                >
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Prossima partita
                    </div>
                    <div className="font-display text-2xl font-bold mt-2">
                        Nessuna partita in programma
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                        Nuove partite compariranno qui non appena verranno inserite.
                    </div>
                </div>
            )}

            {/* Materials checklist */}
            {nextMatch && nextMatch.materials?.length > 0 && (
                <section>
                    <SectionHeader
                        eyebrow="Da preparare"
                        title="Cosa portare"
                    />
                    <div className="rounded-2xl hairline bg-card p-4 md:p-5 mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {nextMatch.materials.map((item) => (
                            <label
                                key={item}
                                data-testid="materials-checklist-item"
                                className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-muted/60 press cursor-pointer"
                            >
                                <Checkbox
                                    checked={!!checked[item]}
                                    onCheckedChange={(v) =>
                                        setChecked((c) => ({ ...c, [item]: !!v }))
                                    }
                                />
                                <span
                                    className={`text-sm ${
                                        checked[item]
                                            ? "line-through text-muted-foreground"
                                            : ""
                                    }`}
                                >
                                    {item}
                                </span>
                            </label>
                        ))}
                    </div>
                </section>
            )}

            {/* Latest news */}
            <section>
                <div className="flex items-end justify-between">
                    <SectionHeader eyebrow="Ultime" title="News" />
                    <Link
                        to="/news"
                        className="text-xs uppercase tracking-widest text-primary flex items-center gap-1 press"
                    >
                        Tutte <ArrowRight size={14} weight="bold" />
                    </Link>
                </div>
                <div className="mt-3 grid gap-3">
                    {news.slice(0, 3).map((n) => (
                        <Link
                            key={n.id}
                            to="/news"
                            className="group flex gap-4 rounded-xl hairline bg-card p-3 press hover:border-primary/40"
                        >
                            <div
                                className="w-20 h-20 shrink-0 rounded-lg bg-muted bg-cover bg-center"
                                style={{
                                    backgroundImage: n.image_url
                                        ? `url(${n.image_url})`
                                        : undefined,
                                }}
                            />
                            <div className="min-w-0 flex-1">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                    {n.author || "Società"}
                                </div>
                                <div className="font-display text-lg font-bold leading-tight mt-0.5 truncate">
                                    {n.title}
                                </div>
                                <div className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                    {n.body}
                                </div>
                            </div>
                        </Link>
                    ))}
                    {news.length === 0 && (
                        <div className="text-sm text-muted-foreground">
                            Nessuna news pubblicata al momento.
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

function SectionHeader({ eyebrow, title }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {eyebrow}
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mt-1">
                {title}
            </h2>
        </div>
    );
}

function NextMatchCard({ match, convStatus, canRespond, onConfirm, onDecline }) {
    return (
        <div
            data-testid="next-match-hero-card"
            className="relative overflow-hidden rounded-2xl hairline"
        >
            <img
                src="https://images.pexels.com/photos/28827841/pexels-photo-28827841.jpeg"
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-background via-background/70 to-primary/15" />
            <div className="relative p-5 md:p-7">
                <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-primary flex items-center gap-2">
                        <Trophy size={14} weight="fill" /> Prossima partita
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-full bg-primary/15 text-primary flex items-center gap-1">
                        {match.home ? <House size={12} weight="fill" /> : <Airplane size={12} weight="fill" />}
                        {match.home ? "Casa" : "Trasferta"}
                    </span>
                </div>
                <div className="mt-3">
                    <div className="num text-4xl md:text-6xl font-black leading-none uppercase">
                        {match.kickoff_time}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 capitalize">
                        {formatItalianDate(match.date)}
                    </div>
                </div>
                <div className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mt-4">
                    vs <span className="text-primary">{match.opponent}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                        <MapPin size={16} weight="duotone" />{" "}
                        {match.field_name || "Campo da definire"}
                    </span>
                    {match.meeting_time && (
                        <span className="flex items-center gap-1.5">
                            <Clock size={16} weight="duotone" /> Ritrovo{" "}
                            {match.meeting_time}
                        </span>
                    )}
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-2">
                    <Link
                        to={`/partite/${match.id}`}
                        className="inline-flex items-center justify-center gap-2 rounded-lg hairline bg-background/60 hover:bg-background px-4 h-11 text-xs uppercase tracking-widest font-semibold press"
                    >
                        Dettagli <ArrowRight size={14} weight="bold" />
                    </Link>
                    {canRespond && (
                        <>
                            <Button
                                data-testid="rsvp-confirm-button"
                                onClick={onConfirm}
                                className={`h-11 flex-1 text-xs uppercase tracking-widest press ${
                                    convStatus === "confirmed"
                                        ? "bg-primary text-primary-foreground"
                                        : ""
                                }`}
                            >
                                <CheckCircle size={16} weight="fill" className="mr-1" />
                                {convStatus === "confirmed" ? "Confermato" : "Ci sarò"}
                            </Button>
                            <Button
                                data-testid="rsvp-decline-button"
                                onClick={onDecline}
                                variant="outline"
                                className={`h-11 flex-1 text-xs uppercase tracking-widest press ${
                                    convStatus === "declined" ? "border-destructive text-destructive" : ""
                                }`}
                            >
                                <XCircle size={16} weight="fill" className="mr-1" />
                                {convStatus === "declined" ? "Assente" : "Non ci sarò"}
                            </Button>
                        </>
                    )}
                    {!canRespond && convStatus && (
                        <div className="text-xs uppercase tracking-widest text-muted-foreground self-center">
                            Convocazione: {convStatus === "confirmed" ? "confermata" : convStatus}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
